import { useEffect, useState, useCallback } from "react";
import api, { fmtMXNFull, fmtDate, todayLocal } from "@/lib/api";
import { Wallet, X, FileDown, Loader2, HandCoins, Undo2, Receipt, UserPlus, Pencil, Phone, Warehouse } from "lucide-react";
import { toast } from "sonner";

const KIND = {
  entrega: { label: "ENTREGA", color: "#22c55e" },
  reembolso: { label: "REEMBOLSO", color: "#22d3ee" },
  gasto_fondo: { label: "GASTO · FONDO", color: "#fbbf24" },
  gasto_propio: { label: "GASTO · SU DINERO", color: "#ef4444" },
};

export default function WorkshopMechanicsTab({ canWrite }) {
  const [accounts, setAccounts] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMove, setOpenMove] = useState(null); // {type, mechanic?}
  const [openMech, setOpenMech] = useState(null); // {} nuevo | mecánico a editar
  const [mf, setMf] = useState({ name: "", workshop_name: "", phone: "" });
  const [statement, setStatement] = useState(null); // {summary, movements}
  const [downloading, setDownloading] = useState(false);
  const [f, setF] = useState({ mechanic: "", amount: "", movement_date: todayLocal(), notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, m] = await Promise.all([
        api.get("/workshop/mechanics/accounts"),
        api.get("/workshop/mechanics"),
      ]);
      setAccounts(a.data);
      setMechanics(m.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openMovement = (type, mechanic = "") => {
    setF({ mechanic: mechanic || mechanics[0]?.name || "", amount: "", movement_date: todayLocal(), notes: "" });
    setOpenMove({ type });
  };

  const openMechForm = (m = null) => {
    setMf(m ? { name: m.name, workshop_name: m.workshop_name || "", phone: m.phone || "" } : { name: "", workshop_name: "", phone: "" });
    setOpenMech(m || {});
  };

  const saveMech = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { name: mf.name.trim(), workshop_name: mf.workshop_name.trim() || null, phone: mf.phone.trim() || null };
      if (openMech?.id) {
        await api.patch(`/workshop/mechanics/${openMech.id}`, body);
        toast.success("Mecánico actualizado");
      } else {
        await api.post("/workshop/mechanics", body);
        toast.success("Mecánico registrado");
      }
      setOpenMech(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/workshop/mechanics/${a.mechanic_id}`, { active: !a.active });
      toast.success(a.active ? "Mecánico desactivado" : "Mecánico reactivado");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const saveMove = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/workshop/fund-movements", {
        type: openMove.type, mechanic: f.mechanic.trim(), amount: Number(f.amount),
        movement_date: f.movement_date, notes: f.notes || null,
      });
      toast.success(openMove.type === "entrega" ? "Entrega de dinero registrada" : "Reembolso registrado");
      setOpenMove(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const openStatement = async (mechanic) => {
    try {
      const { data } = await api.get("/workshop/mechanics/statement", { params: { mechanic } });
      setStatement(data);
    } catch { toast.error("Error al cargar el estado de cuenta"); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/workshop/mechanics/statement/pdf", { params: { mechanic: statement.summary.mechanic }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cuenta_${statement.summary.mechanic.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Estado de cuenta descargado");
    } catch { toast.error("Error al generar el PDF"); }
    finally { setDownloading(false); }
  };

  const Stat = ({ label, value, color }) => (
    <div className="panel p-2.5">
      <div className="label-mono" style={{ color: "var(--text-mute)" }}>{label}</div>
      <div className="font-display font-bold text-base tabular-nums" style={{ color }}>{fmtMXNFull(value)}</div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="workshop-mechanics-tab">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-400">Directorio de mecánicos y control de dinero entregado, gastos y reembolsos.</span>
        {canWrite && (
          <div className="ml-auto flex gap-2">
            <button data-testid="new-mechanic-btn" onClick={() => openMechForm()}
              className="btn-primary px-3 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5">
              <UserPlus className="w-3.5 h-3.5" /> Registrar mecánico
            </button>
            <button data-testid="new-entrega-btn" onClick={() => openMovement("entrega")}
              className="btn-ghost px-3 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5" style={{ color: "var(--green)" }}>
              <HandCoins className="w-3.5 h-3.5" /> Entregar dinero
            </button>
            <button data-testid="new-reembolso-btn" onClick={() => openMovement("reembolso")}
              className="btn-ghost px-3 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5" style={{ color: "var(--cyan)" }}>
              <Undo2 className="w-3.5 h-3.5" /> Reembolsar
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
      ) : accounts.length === 0 ? (
        <div className="panel text-center py-12 text-slate-500">
          Sin mecánicos todavía. <b>Registra un mecánico</b> con su nombre y el de su taller.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {accounts.map((a) => (
            <div key={a.mechanic} className="panel p-4 space-y-3" style={a.registered && a.active === false ? { opacity: 0.55 } : undefined} data-testid={`mechanic-card-${a.mechanic.replace(/\s+/g, "-")}`}>
              <div className="flex items-start gap-2">
                <div className="w-9 h-9 flex items-center justify-center rounded-sm shrink-0" style={{ background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.25)" }}>
                  <Wallet className="w-4 h-4 text-cyan-300" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-slate-100 flex items-center gap-2">
                    {a.mechanic}
                    {a.registered && a.active === false && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-sm tracking-widest" style={{ color: "var(--red)", border: "1px solid var(--red)" }}>INACTIVO</span>}
                    {!a.registered && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-sm tracking-widest" style={{ color: "var(--amber)", border: "1px solid var(--amber)" }} data-testid={`mechanic-unregistered-${a.mechanic.replace(/\s+/g, "-")}`}>SIN REGISTRAR</span>}
                  </div>
                  <div className="text-[11px] text-slate-400 flex flex-wrap items-center gap-x-3">
                    {a.workshop_name && <span className="flex items-center gap-1" data-testid={`mechanic-workshop-${a.mechanic.replace(/\s+/g, "-")}`}><Warehouse className="w-3 h-3" style={{ color: "var(--amber)" }} /> {a.workshop_name}</span>}
                    {a.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {a.phone}</span>}
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-1 shrink-0">
                  {canWrite && a.registered && (
                    <button data-testid={`mechanic-edit-${a.mechanic.replace(/\s+/g, "-")}`} title="Editar mecánico"
                      onClick={() => openMechForm(mechanics.find((m) => m.id === a.mechanic_id) || { id: a.mechanic_id, name: a.mechanic, workshop_name: a.workshop_name, phone: a.phone })}
                      className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--text-dim)" }}><Pencil className="w-3.5 h-3.5" /></button>
                  )}
                  <button data-testid={`mechanic-statement-${a.mechanic.replace(/\s+/g, "-")}`} onClick={() => openStatement(a.mechanic)}
                    className="btn-ghost px-2.5 py-1.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1" style={{ color: "var(--cyan)" }}>
                    <Receipt className="w-3.5 h-3.5" /> Estado de cuenta
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Stat label="ENTREGADO" value={a.entregado} color="var(--text)" />
                <Stat label="GASTÓ DEL FONDO" value={a.gastado_fondo} color="var(--amber)" />
                <Stat label="EN SU PODER" value={a.saldo_fondo} color={a.saldo_fondo >= 0 ? "var(--green)" : "var(--red)"} />
                <Stat label="PUSO DE SU BOLSA" value={a.puso_de_su_bolsa} color="var(--text)" />
                <Stat label="REEMBOLSADO" value={a.reembolsado} color="var(--cyan)" />
                <Stat label="POR REEMBOLSARLE" value={a.por_reembolsar} color={a.por_reembolsar > 0 ? "var(--red)" : "var(--green)"} />
              </div>
              {canWrite && (
                <div className="flex gap-2">
                  <button onClick={() => openMovement("entrega", a.mechanic)} className="btn-ghost flex-1 py-1.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold" style={{ color: "var(--green)" }}>+ Entregar</button>
                  {a.por_reembolsar > 0 && (
                    <button onClick={() => openMovement("reembolso", a.mechanic)} className="btn-ghost flex-1 py-1.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold" style={{ color: "var(--cyan)" }}>Reembolsar</button>
                  )}
                  {a.registered && (
                    <button data-testid={`mechanic-toggle-${a.mechanic.replace(/\s+/g, "-")}`} onClick={() => toggleActive(a)}
                      className="btn-ghost px-2.5 py-1.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold" style={{ color: a.active === false ? "var(--green)" : "var(--red)" }}>
                      {a.active === false ? "Reactivar" : "Desactivar"}
                    </button>
                  )}
                  {!a.registered && (
                    <button data-testid={`mechanic-register-${a.mechanic.replace(/\s+/g, "-")}`} onClick={() => { setMf({ name: a.mechanic, workshop_name: "", phone: "" }); setOpenMech({}); }}
                      className="btn-ghost px-2.5 py-1.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold" style={{ color: "var(--amber)" }}>Registrar</button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {openMech !== null && (
        <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={() => setOpenMech(null)}>
          <form onSubmit={saveMech} className="panel-strong w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="mechanic-form">
            <div className="panel-header px-6 py-4 flex items-center justify-between">
              <div>
                <div className="label-mono">// DIRECTORIO</div>
                <h2 className="font-display font-bold text-xl text-slate-100">{openMech?.id ? "Editar mecánico" : "Registrar mecánico"}</h2>
              </div>
              <button type="button" onClick={() => setOpenMech(null)} className="text-slate-500 hover:text-cyan-300"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <label className="label-mono block mb-1.5">Nombre del mecánico *</label>
                <input required value={mf.name} onChange={(e) => setMf({ ...mf, name: e.target.value })}
                  className="w-full input-cyber px-3 py-2 text-sm uppercase" data-testid="mech-name" placeholder="JUAN PÉREZ" />
              </div>
              <div>
                <label className="label-mono block mb-1.5">Nombre de su taller</label>
                <input value={mf.workshop_name} onChange={(e) => setMf({ ...mf, workshop_name: e.target.value })}
                  className="w-full input-cyber px-3 py-2 text-sm uppercase" data-testid="mech-workshop" placeholder="TALLER LOS AMIGOS" />
              </div>
              <div>
                <label className="label-mono block mb-1.5">Teléfono</label>
                <input value={mf.phone} onChange={(e) => setMf({ ...mf, phone: e.target.value })}
                  className="w-full input-cyber px-3 py-2 text-sm font-mono" data-testid="mech-phone" placeholder="747 123 4567" />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setOpenMech(null)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
                <button type="submit" disabled={saving} data-testid="mech-save" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">{saving ? "Guardando…" : "Guardar"}</button>
              </div>
            </div>
          </form>
        </div>
      )}

      {openMove && (
        <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={() => setOpenMove(null)}>
          <form onSubmit={saveMove} className="panel-strong w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="fund-movement-form">
            <div className="panel-header px-6 py-4 flex items-center justify-between">
              <div>
                <div className="label-mono">// CUENTA MECÁNICO</div>
                <h2 className="font-display font-bold text-xl text-slate-100">{openMove.type === "entrega" ? "Entregar dinero" : "Registrar reembolso"}</h2>
              </div>
              <button type="button" onClick={() => setOpenMove(null)} className="text-slate-500 hover:text-cyan-300"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <label className="label-mono block mb-1.5">Mecánico *</label>
                <select required value={f.mechanic} onChange={(e) => setF({ ...f, mechanic: e.target.value })}
                  className="w-full input-cyber px-3 py-2 text-sm" data-testid="fund-mechanic">
                  <option value="" style={{ background: "#0a0f1d" }}>— Selecciona un mecánico —</option>
                  {f.mechanic && !mechanics.some((m) => m.name === f.mechanic) && (
                    <option value={f.mechanic} style={{ background: "#0a0f1d" }}>{f.mechanic} (sin registrar)</option>
                  )}
                  {mechanics.filter((m) => m.active !== false).map((m) => (
                    <option key={m.id} value={m.name} style={{ background: "#0a0f1d" }}>{m.name}{m.workshop_name ? ` · ${m.workshop_name}` : ""}</option>
                  ))}
                </select>
                {mechanics.length === 0 && <div className="text-[10px] mt-1" style={{ color: "var(--amber)" }}>No hay mecánicos registrados — usa "Registrar mecánico" primero.</div>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-mono block mb-1.5">Monto MXN *</label>
                  <input required type="number" step="0.01" min="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })}
                    className="w-full input-cyber px-3 py-2 text-sm text-right" data-testid="fund-amount" />
                </div>
                <div>
                  <label className="label-mono block mb-1.5">Fecha *</label>
                  <input required type="date" value={f.movement_date} onChange={(e) => setF({ ...f, movement_date: e.target.value })}
                    className="w-full input-cyber px-3 py-2 text-sm" data-testid="fund-date" />
                </div>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Nota</label>
                <input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm"
                  data-testid="fund-notes" placeholder="para compra de refacciones…" />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setOpenMove(null)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
                <button type="submit" disabled={saving} data-testid="fund-save" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">{saving ? "Guardando…" : "Guardar"}</button>
              </div>
            </div>
          </form>
        </div>
      )}

      {statement && (
        <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={() => setStatement(null)}>
          <div className="panel-strong w-full max-w-2xl overflow-y-auto scan-line" style={{ maxHeight: "calc(100dvh - 2rem)" }} onClick={(e) => e.stopPropagation()} data-testid="mechanic-statement-modal">
            <div className="panel-header px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <div>
                <div className="label-mono">// ESTADO DE CUENTA</div>
                <h2 className="font-display font-bold text-xl text-slate-100">{statement.summary.mechanic}</h2>
                {(statement.summary.workshop_name || statement.summary.phone) && (
                  <div className="text-[11px] text-slate-400 flex items-center gap-3 mt-0.5" data-testid="statement-mechanic-info">
                    {statement.summary.workshop_name && <span className="flex items-center gap-1"><Warehouse className="w-3 h-3" style={{ color: "var(--amber)" }} /> {statement.summary.workshop_name}</span>}
                    {statement.summary.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {statement.summary.phone}</span>}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={downloadPdf} disabled={downloading} data-testid="mechanic-pdf-btn"
                  className="btn-ghost px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-widest font-semibold flex items-center gap-1.5 disabled:opacity-50"
                  style={{ color: "var(--green)", borderColor: "rgba(34,197,94,0.4)" }}>
                  {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} PDF
                </button>
                <button onClick={() => setStatement(null)} className="text-slate-500 hover:text-cyan-300"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <Stat label="EN SU PODER" value={statement.summary.saldo_fondo} color={statement.summary.saldo_fondo >= 0 ? "var(--green)" : "var(--red)"} />
                <Stat label="POR REEMBOLSARLE" value={statement.summary.por_reembolsar} color={statement.summary.por_reembolsar > 0 ? "var(--red)" : "var(--green)"} />
                <Stat label="ENTREGADO TOTAL" value={statement.summary.entregado} color="var(--text)" />
              </div>
              <div className="space-y-1.5" data-testid="statement-timeline">
                {statement.movements.length === 0 && <div className="text-center py-8 text-slate-500 panel">Sin movimientos.</div>}
                {statement.movements.map((m) => {
                  const k = KIND[m.kind] || { label: m.kind, color: "#94a3b8" };
                  return (
                    <div key={m.id + m.kind} className="panel p-2.5 flex items-center gap-3">
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm tracking-widest shrink-0" style={{ color: k.color, border: `1px solid ${k.color}` }}>{k.label}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-slate-200 truncate">{m.description}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{fmtDate(m.date)}{m.by ? ` · ${m.by}` : ""}</div>
                      </div>
                      <span className="font-mono text-sm text-slate-100 shrink-0">{fmtMXNFull(m.amount)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
